// ═══════════════════════════════════════════════════════════════════════════
// Importar leituras de estação CONVENCIONAL pela planilha padrão (28/09/2026)
// ═══════════════════════════════════════════════════════════════════════════
// Pedido do Lucas: "precisamos ter um padrão para que seja inputado e haja um
// filtro que inabilite o envio se o dado não estiver no padrão".
//
// Formato (escolha do Lucas): LISTA — uma linha por leitura, uma estação por
// planilha, régua OU chuva por planilha. O modelo sai daqui já com o código da
// estação e o título certo.
//
// Quem decide se a planilha está no padrão é o SERVIDOR
// (comercial-engine/handlers/estacoesPlanilha.js), numa prévia (`ensaio`) que
// não grava. Esta tela só lê as células do Excel e mostra o que voltou: não há
// uma segunda régua aqui para discordar da primeira. O botão de enviar fica
// travado enquanto houver um problema que seja.
//
// ⚠️ As células vão CRUAS (`raw: true`): data do Excel vai como o número de
// série dele, e quem converte é o servidor, em dia/mês/ano. Deixar o navegador
// transformar em Date traz o fuso da máquina e o palpite americano de mês/dia.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  App as AntApp, Alert, Button, Checkbox, Col, Modal, Row, Segmented, Select, Space, Table, Tag, Typography,
} from 'antd';
import { DownloadOutlined, UploadOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { api } from './shared';

const { Text } = Typography;

type Tipo = 'nivel' | 'chuva';

export interface EstacaoImportavel {
  id: string; codigo: string; nome: string; unidade?: string | null; tipos: string[];
}

interface Problema { linha: number | null; coluna?: string | null; valor?: any; msg: string }
interface Previa {
  estacao: string;
  unidade_arquivo: string | null;
  unidade_gravada: string;
  converte: boolean;
  resumo: { n: number; de: string; ate: string; min: number; max: number } | null;
  erros: Problema[];
  total_erros: number;
  avisos: Problema[];
  amostra: { linha: number; lido_local: string; valor: number; observacao: string | null }[];
}
interface Arquivo {
  nome: string;
  aba: string;
  cabecalho: any[];
  linhas: { linha: number; c: any[] }[];
  codigo: string | null;
  data1904: boolean;
}

const norm = (s: any) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const vazia = (v: any) => v === null || v === undefined || String(v).trim() === '';

// 'AAAA-MM-DD HH:MM' → '01/09/2026 07:00'
const fmtLocal = (s?: string | null) => {
  if (!s) return '—';
  const [d, h] = s.split(' ');
  const [a, m, dia] = d.split('-');
  return `${dia}/${m}/${a} ${h}`;
};
const fmtNum = (v: number, unidade: string) =>
  `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 4 })} ${unidade}`;

function tiposPossiveis(est: EstacaoImportavel): Tipo[] {
  const t: Tipo[] = [];
  if (est.tipos.some((x) => x === 'fluviometrica' || x === 'linimetrica')) t.push('nivel');
  if (est.tipos.includes('pluviometrica')) t.push('chuva');
  return t;
}

// ── O modelo (.xlsx) ─────────────────────────────────────────────────────────
function baixarModelo(est: EstacaoImportavel, tipo: Tipo) {
  const unidade = tipo === 'nivel' ? (est.unidade || 'm') : 'mm';
  const colValor = tipo === 'nivel' ? `Leitura (${unidade})` : 'Chuva (mm)';
  const exemplo = tipo === 'nivel' ? '1,24' : '18,4';
  const ws = XLSX.utils.aoa_to_sheet([['Data', 'Hora', colValor, 'Observação']]);
  ws['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 48 }];

  const instr = XLSX.utils.aoa_to_sheet([
    // B1 é lido de volta: é o que impede subir o modelo de uma estação em outra.
    ['Estação', est.codigo],
    ['Nome', est.nome],
    ['Esta planilha carrega', tipo === 'nivel' ? `Leituras de régua (nível), em ${unidade}` : 'Chuva do pluviômetro (24 h), em mm'],
    [],
    ['Coluna', 'Como preencher', 'Exemplo'],
    ['Data', 'Dia/mês/ano. UMA linha para cada leitura.', '01/09/2026'],
    ['Hora', 'Hora em que a leitura foi feita, relógio de 24 h. Vale 7, 7h, 07:00, 17h30.', '07:00'],
    [colValor, tipo === 'nivel'
      ? 'Só o número, com vírgula ou ponto. Leu em centímetros? Troque o título para "Leitura (cm)" que o sistema converte.'
      : 'Só o número, com vírgula ou ponto. Leitura das 07h = chuva das 24 horas anteriores.', exemplo],
    ['Observação', 'Opcional. Qualquer informação extra vai aqui — não crie colunas novas.', 'régua suja'],
    [],
    ['O ENVIO É BLOQUEADO SE:'],
    ['• faltar data, hora ou leitura em alguma linha (se não houve leitura, apague a linha)'],
    ['• a data não existir (31/02) ou estiver no futuro'],
    ['• a leitura tiver texto junto do número'],
    ['• o mesmo dia e hora aparecer duas vezes, ou já estiver no sistema'],
    ['• existir coluna fora do modelo com dados'],
    ['• o arquivo for o modelo de outra estação'],
    [],
    ['Não renomeie a aba "Leituras" nem mude a primeira linha dela.'],
  ]);
  instr['!cols'] = [{ wch: 24 }, { wch: 90 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leituras');
  XLSX.utils.book_append_sheet(wb, instr, 'Como preencher');
  XLSX.writeFile(wb, `modelo-${tipo === 'nivel' ? 'regua' : 'chuva'}-${est.codigo}.xlsx`);
}

// ── Leitura do arquivo (sem interpretar nada) ────────────────────────────────
async function lerArquivo(file: File): Promise<Arquivo> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
  const aba = wb.SheetNames.find((n) => norm(n) === 'leituras') ?? wb.SheetNames[0];
  const ws = aba ? wb.Sheets[aba] : undefined;
  if (!ws || !ws['!ref']) throw new Error('a planilha está vazia');
  const primeira = XLSX.utils.decode_range(ws['!ref']).s.r;     // linha do título (0 = linha 1)
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null, blankrows: true });
  const instr = wb.Sheets['Como preencher'];
  const codigo = instr?.B1?.v != null ? String(instr.B1.v).trim() : null;
  return {
    nome: file.name,
    aba,
    cabecalho: aoa[0] ?? [],
    // Número da linha = o do Excel, para a pessoa achar o problema na planilha.
    // Linha toda em branco não vai (é o rabo formatado que todo Excel tem).
    linhas: aoa.slice(1).map((c, i) => ({ linha: primeira + 2 + i, c: c ?? [] }))
      .filter((l) => l.c.some((x) => !vazia(x))),
    codigo,
    data1904: !!(wb as any).Workbook?.WBProps?.date1904,
  };
}

function lerErroApi(e: any): any {
  const d = e?.response?.data;
  if (d?.body && typeof d.body === 'string') { try { return JSON.parse(d.body); } catch { /* segue */ } }
  return d;
}

export default function ImportarPlanilhaLeituras({ estacao, onClose, onImportado }: {
  estacao: EstacaoImportavel; onClose: () => void; onImportado: () => void;
}) {
  const { message } = AntApp.useApp();
  const tipos = useMemo(() => tiposPossiveis(estacao), [estacao]);
  const [tipo, setTipo] = useState<Tipo>(tipos[0] ?? 'nivel');
  const [modo, setModo] = useState<string>('adicionar');
  const [arquivo, setArquivo] = useState<Arquivo | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [falhaPrevia, setFalhaPrevia] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [conferiAvisos, setConferiAvisos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  const corpo = (extra: Record<string, any>) => arquivo && ({
    estacao_id: estacao.id, tipo, modo,
    cabecalho: arquivo.cabecalho, linhas: arquivo.linhas,
    codigo_no_arquivo: arquivo.codigo, data1904: arquivo.data1904, ...extra,
  });

  // A prévia refaz sempre que muda o arquivo, o tipo ou o modo: a repetição
  // contra o que já está gravado só existe no "adicionar".
  useEffect(() => {
    if (!arquivo) { setPrevia(null); return; }
    const meu = ++seq.current;
    setConferindo(true); setFalhaPrevia(null); setConferiAvisos(false);
    api('op_estacao_leituras_importar', corpo({ ensaio: true }))
      .then((r: Previa) => { if (meu === seq.current) setPrevia(r); })
      .catch((e: any) => {
        if (meu !== seq.current) return;
        setPrevia(null);
        setFalhaPrevia(lerErroApi(e)?.error || e.message);
      })
      .finally(() => { if (meu === seq.current) setConferindo(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arquivo, tipo, modo]);

  async function escolherArquivo(f?: File | null) {
    if (!f) return;
    try {
      setArquivo(await lerArquivo(f));
    } catch (e: any) {
      setArquivo(null);
      message.error(`Não consegui abrir ${f.name}: ${e.message}. Use o modelo (.xlsx).`);
    } finally {
      if (inputRef.current) inputRef.current.value = '';      // permite subir o mesmo arquivo corrigido
    }
  }

  const temErro = !!previa && previa.total_erros > 0;
  const temAviso = !!previa && previa.avisos.length > 0;
  const liberado = !!previa && !conferindo && !temErro && !!previa.resumo && (!temAviso || conferiAvisos);

  async function enviar() {
    if (!liberado) return;
    setEnviando(true);
    try {
      const r = await api('op_estacao_leituras_importar', corpo({ confirmar_avisos: conferiAvisos }));
      message.success(`${r.importadas} leitura(s) gravada(s)${r.substituidas ? ` · ${r.substituidas} substituída(s)` : ''}.`);
      onImportado();
    } catch (e: any) {
      const d = lerErroApi(e);
      // A planilha mudou entre a prévia e o envio (ou alguém lançou leitura no
      // meio): o servidor devolve a conferência nova, e ela vira a prévia.
      if (d?.erros) { setPrevia(d); setConferiAvisos(false); }
      message.error(d?.error || e.message);
    } finally { setEnviando(false); }
  }

  const unidade = previa?.unidade_gravada ?? (tipo === 'nivel' ? (estacao.unidade || 'm') : 'mm');

  return (
    <Modal open onCancel={onClose} width="94vw" style={{ maxWidth: 1180, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      title={`Importar leituras pela planilha — ${estacao.codigo}`}
      okText={previa?.resumo && !temErro ? `Gravar ${previa.resumo.n.toLocaleString('pt-BR')} leitura(s)` : 'Gravar'}
      okButtonProps={{ disabled: !liberado }} confirmLoading={enviando} onOk={enviar} cancelText="Fechar">
      {!tipos.length ? (
        <Alert type="warning" showIcon message="Esta estação não está cadastrada como fluviométrica nem pluviométrica."
          description="Ajuste o tipo no cadastro da estação para poder importar leituras." />
      ) : (
      <Row gutter={[24, 16]}>
        {/* ── Passos ── */}
        <Col xs={24} lg={9}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {tipos.length > 1 && (
              <div>
                <Text strong>O que você vai subir?</Text><br />
                <Segmented value={tipo} onChange={(v) => setTipo(v as Tipo)} style={{ marginTop: 6 }}
                  options={[{ value: 'nivel', label: 'Régua (nível)' }, { value: 'chuva', label: 'Chuva (pluviômetro)' }]} />
              </div>
            )}
            <div>
              <Text strong>1. Baixe o modelo</Text>
              <div style={{ fontSize: 12, color: '#666', margin: '2px 0 6px' }}>
                Uma linha por leitura: Data · Hora · {tipo === 'nivel' ? `Leitura (${estacao.unidade || 'm'})` : 'Chuva (mm)'} · Observação.
                A aba "Como preencher" explica o que barra o envio.
              </div>
              <Button icon={<DownloadOutlined />} onClick={() => baixarModelo(estacao, tipo)}>
                Baixar modelo de {tipo === 'nivel' ? 'régua' : 'chuva'} (.xlsx)
              </Button>
            </div>
            <div>
              <Text strong>2. Suba a planilha preenchida</Text><br />
              <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={(e) => escolherArquivo(e.target.files?.[0])} />
              <Button type="primary" icon={<UploadOutlined />} style={{ marginTop: 6 }}
                onClick={() => inputRef.current?.click()} loading={conferindo}>
                {arquivo ? 'Trocar arquivo' : 'Escolher arquivo'}
              </Button>
              {arquivo && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {arquivo.nome} · aba "{arquivo.aba}" · {arquivo.linhas.length.toLocaleString('pt-BR')} linha(s) preenchida(s)
                </div>
              )}
            </div>
            <div>
              <Text strong>3. O que fazer com o que já existe</Text><br />
              <Select value={modo} onChange={setModo} style={{ width: '100%', marginTop: 6 }}
                showSearch optionFilterProp="label"
                options={[
                  { value: 'adicionar', label: 'Adicionar — recusa leitura que já existe' },
                  { value: 'substituir_periodo', label: 'Substituir o período da planilha' },
                  { value: 'substituir_estacao', label: `Substituir TODA a série de ${tipo === 'nivel' ? 'régua' : 'chuva'}` },
                ]} />
              {modo !== 'adicionar' && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                  ⚠ Apaga {modo === 'substituir_estacao'
                    ? `todas as leituras de ${tipo === 'nivel' ? 'régua' : 'chuva'} desta estação`
                    : 'as leituras do período da planilha'} antes de gravar. Tudo numa operação só: se der erro, nada muda.
                </div>
              )}
            </div>
          </Space>
        </Col>

        {/* ── Resultado da conferência ── */}
        <Col xs={24} lg={15}>
          {!arquivo && (
            <Alert type="info" showIcon message="Nenhum arquivo ainda"
              description="Suba a planilha e o sistema confere linha por linha antes de liberar o envio." />
          )}
          {falhaPrevia && <Alert type="error" showIcon message="Não foi possível conferir a planilha" description={falhaPrevia} />}
          {previa && !conferindo && (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {temErro ? (
                <Alert type="error" showIcon icon={<CloseCircleFilled />}
                  message={`${previa.total_erros.toLocaleString('pt-BR')} problema(s) na planilha — o envio está bloqueado`}
                  description="Corrija na planilha e suba o arquivo de novo. Nada foi gravado." />
              ) : previa.resumo && (
                <Alert type="success" showIcon icon={<CheckCircleFilled />}
                  message={`${previa.resumo.n.toLocaleString('pt-BR')} leitura(s) prontas para gravar`}
                  description={<>
                    De {fmtLocal(previa.resumo.de)} a {fmtLocal(previa.resumo.ate)} · menor {fmtNum(previa.resumo.min, unidade)} ·
                    maior {fmtNum(previa.resumo.max, unidade)}
                    {previa.converte && <><br />O arquivo está em <b>{previa.unidade_arquivo}</b>; será gravado em <b>{unidade}</b> (os valores abaixo já estão convertidos).</>}
                  </>} />
              )}

              {temErro && (
                <Table size="small" pagination={false}
                  rowKey={(e: Problema) => `${e.linha ?? 'arquivo'}|${e.coluna ?? ''}|${e.msg}`}
                  scroll={{ y: 'clamp(200px, calc(100vh - 420px), 560px)' }}
                  dataSource={previa.erros}
                  footer={previa.total_erros > previa.erros.length
                    ? () => `Mostrando os ${previa.erros.length} primeiros de ${previa.total_erros.toLocaleString('pt-BR')} problemas.`
                    : undefined}
                  columns={[
                    { title: 'Linha', dataIndex: 'linha', width: 70, render: (v) => v ?? <Tag>arquivo</Tag> },
                    { title: 'Coluna', dataIndex: 'coluna', width: 120, render: (v) => v || '—' },
                    { title: 'Na planilha', dataIndex: 'valor', width: 130,
                      render: (v) => (v === undefined || v === null || v === '' ? '—' : <Text code>{String(v).slice(0, 30)}</Text>) },
                    { title: 'O que corrigir', dataIndex: 'msg' },
                  ]} />
              )}

              {temAviso && (
                <Alert type="warning" showIcon message="Avisos — não impedem o envio, mas confira"
                  description={<>
                    <ul style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
                      {previa.avisos.map((a, i) => <li key={i}>{a.msg}</li>)}
                    </ul>
                    {!temErro && (
                      <Checkbox checked={conferiAvisos} onChange={(e) => setConferiAvisos(e.target.checked)}>
                        Conferi os avisos e quero gravar assim mesmo
                      </Checkbox>
                    )}
                  </>} />
              )}

              {!temErro && previa.amostra.length > 0 && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Como o sistema entendeu {previa.resumo && previa.resumo.n > previa.amostra.length ? '(primeiras e últimas linhas)' : ''}:
                  </Text>
                  <Table size="small" rowKey="linha" pagination={false} style={{ marginTop: 4 }}
                    dataSource={previa.amostra}
                    columns={[
                      { title: 'Linha', dataIndex: 'linha', width: 70 },
                      { title: 'Data e hora', dataIndex: 'lido_local', width: 160, render: fmtLocal },
                      { title: tipo === 'nivel' ? 'Leitura' : 'Chuva', dataIndex: 'valor', width: 130,
                        render: (v: number) => fmtNum(v, unidade) },
                      { title: 'Observação', dataIndex: 'observacao', render: (v) => v || '' },
                    ]} />
                </div>
              )}
            </Space>
          )}
        </Col>
      </Row>
      )}
    </Modal>
  );
}
